<template>
  <div>
    <HMModalHeader
      :title="$t('STUDIO_SIGNUP.TITLE.REGISTER_YOUR_ACCOUNT')"
      :closable="false"
    />
    <h1 class="my-4">
      {{ $t('STUDIO_SIGNUP.TITLE.CREATE_YOUR_ACCOUNT_NOW') }}
    </h1>
    <div class="d-flex" v-if="displayLoginLink">
      <p>
        {{ $t('STUDIO_SIGNUP.TEXT.ALREADY_HAVE_AN_ACCOUNT') }}
      </p>
      <NuxtLink
        class="c-coral ms-2"
        :to="{ name: loginName, query: $route.query }"
      >
        {{ $t('GENERAL.LINK.LOGIN') }}
      </NuxtLink>
    </div>
    <HMButton class="social-button w-100" color="social" @click="facebookLogin">
      <img class="me-2 social-icon" src="/assets/images/facebook-icon.svg" />
      {{ $t('LOGIN.BUTTON.SIGN_UP_WITH_FACEBOOK') }}
    </HMButton>
    <HMButton
      class="social-button w-100 mt-2"
      color="social"
      @click="googleLogin"
    >
      <img
        class="me-2 social-icon google"
        src="/assets/images/google_icon.png"
      />
      {{ $t('LOGIN.BUTTON.SIGN_UP_WITH_GOOGLE') }}
    </HMButton>
    <!--
            <HMButton class="w-100 mt-2" color="a" @click="appleLogin">
              <img class="me-2" src="/assets/images/apple-icon.svg" />
              {{ $t('LOGIN.BUTTON.SIGN_IN_WITH_APPLE') }}
            </HMButton>
            -->
    <hr />
    <Form class="row" @submit="signUp" ref="form" v-slot="{ meta }">
      <HMTextInput
        class="col-12 col-md-6"
        rules="required"
        type="text"
        :label="$t('GENERAL.LABEL.FIRST_NAME')"
        name="first_name"
        v-model="firstName"
      />
      <HMTextInput
        class="col-12 col-md-6"
        rules="required"
        type="text"
        :label="$t('GENERAL.LABEL.LAST_NAME')"
        name="last_name"
        v-model="lastName"
      />
      <HMTextInput
        class="col-12"
        rules="required|email"
        :label="$t('GENERAL.LABEL.EMAIL')"
        name="email"
        v-model.trim="email"
      />
      <HMTextInput
        class="col-12"
        rules="required|min:8"
        type="password"
        :label="$t('GENERAL.LABEL.PASSWORD')"
        name="password"
        v-model="password"
      />
      <HMModalFooter
        class="col-12"
        :disabled="!meta.valid || inProgress || !agreeTerms"
        :loading="inProgress"
        :submitButton="$t('STUDIO_SIGNUP.BUTTON.CREATE_YOUR_ACCOUNT')"
        :formError="formError"
      >
        <template v-slot:close>
          <div class="d-flex pe-1">
            <HMCheckBox class="w-auto pe-0" v-model="agreeTerms">
              <span>
                {{ $t('STUDIO_SIGNUP.LABEL.I_AGREE_TO_THE') }}
                <a
                  class="c-charcoal"
                  target="_blank"
                  @click.stop
                  :href="config.termsOfUseURL"
                >
                  {{ $t('STUDIO_SIGNUP.LINK.TERMS_OF_SERVICE') }}.
                </a>
              </span>
            </HMCheckBox>
          </div>
        </template>
      </HMModalFooter>
    </Form>
  </div>
</template>

<script lang="ts">
import { Form } from 'vee-validate';
import { errorToMessage } from '@/utils/errors';
import { socialLoginErrorMessage } from '@/utils/social-login';
import { mapState } from 'pinia';
import { isObject } from 'lodash-es';
import { getConfig } from '@/utils/configuration';
import { useStudioStore, useUserStore } from '@/stores';

export default {
  components: {
    Form,
  },
  props: {
    onSignupFn: null,
    loginName: {
      type: String,
      default: 'studio-login',
    },
    displayLoginLink: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      formError: null,
      inProgress: false,
      agreeTerms: false,
    };
  },
  setup() {
    const url = useRequestURL();

    return {
      config: getConfig(url),
    };
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
    }),
    styles() {
      const photoSrc =
        this.studio?.cover_photo?.storage_url ??
        this.studio?.cover_photo?.embedded_image_url;
      if (photoSrc) {
        return {
          backgroundImage: `url(${photoSrc})`,
        };
      } else return {};
    },
    studioURL() {
      return this.$studioURL;
    },
  },
  methods: {
    async signUp() {
      console.log('signUp');
      this.inProgress = true;

      const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
      const reCaptchaToken = await window.grecaptcha?.execute(reCaptchaKey, {
        action: 'studentsignup',
      });

      const data = {
        agree_terms: this.agreeTerms,
        email: this.email,
        password: this.password,
        password1: this.password,
        studio_url: this.studio.studio_url,
        first_name: this.firstName,
        last_name: this.lastName,
        token: reCaptchaToken,
      };
      const userData = {
        student: {
          first_name: this.firstName,
          last_name: this.lastName,
        },
      };

      try {
        const loginResponse = await useUserStore().register(data);
        await useUserStore().$persist();
        await useUserStore().updateStudent(userData);
        this.onLogin(loginResponse);
      } catch (error) {
        const errorMessage = errorToMessage(error.response);
        if (typeof error === 'string') {
          this.formError = errorMessage;
        } else {
          this.formError = null;
          this.$refs.form.setErrors(errorMessage);
        }

        if (isObject(errorMessage)) {
          this.formError = null;
          this.$refs.form.setErrors(errorMessage);
        } else if (errorMessage) {
          this.formError = errorMessage;
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            text: error,
            duration: -1, // stay ON
          });
        }
      } finally {
        this.inProgress = false;
      }
    },
    async onLogin(loginResponse: LoginResponse) {
      await useStudioStore().joinStudio(this.studioURL);
      if (this.onSignupFn) {
        this.$attrs._close()
        this.onSignupFn();
      } else {
        const { user_type } = loginResponse;
        const redirectUrl = this.$route.query.redirect;

        if (redirectUrl) {
          this.$router.replace(redirectUrl);
        } else {
          const name = user_type === 'student' ? 'studio-home-page' : 'dashboard';
          this.$router.replace({ name });
        }
      }
    },
    socialLoginError(error) {
      const text = socialLoginErrorMessage(error, (key) => this.$t(key));
      if (text) {
        this.$notify({
          group: 'app',
          type: 'error',
          text,
        });
      }
    },
    async facebookLogin() {
      try {
        const provider = new this.$firebase.auth.FacebookAuthProvider();
        const result = await this.$firebase.auth().signInWithPopup(provider);
        const loginResponse = await useUserStore().loginWithFacebook(
          result.credential.accessToken
        );
        this.onLogin(loginResponse);
      } catch (error) {
        this.socialLoginError(error);
      }
    },
    async googleLogin() {
      try {
        const provider = new this.$firebase.auth.GoogleAuthProvider();
        const result = await this.$firebase.auth().signInWithPopup(provider);
        const loginResponse = await useUserStore().loginWithGoogle(
          result.credential.accessToken
        );
        this.onLogin(loginResponse);
      } catch (error) {
        this.socialLoginError(error);
      }
    },
    async appleLogin() {
      try {
        const provider = new this.$firebase.auth.OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const result = await this.$firebase.auth().signInWithPopup(provider);
        const loginResponse = await useUserStore().loginWithApple(
          result.credential.accessToken
        );
        this.onLogin(loginResponse);
      } catch (error) {
        this.socialLoginError(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap');

.studio-signup {
  background-color: $light-gray;
  background-size: cover;
  background-position: top center;
}

.social-icon {
  width: 30px;
  height: 20px;
  &.google {
    height: 30px;
  }
}

.social-button {
  font-family: 'Roboto', sans-serif;
}

@include media-breakpoint-down(xs) {
  .content {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>
